var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/*
 * <vitesse-record-grid> — ONE grid of records, used everywhere the product lists things.
 *
 * It has the symbol tree's shape, because that is the product's shape for a list of records: a
 * bordered frame, a grey toolbar holding the search, a header with sortable resizable columns, and
 * rows on a shared column template with dividers between them. The selector is its own COLUMN
 * rather than something tucked inside the row, so every checkbox down the list sits on one axis.
 *
 * IT IS GENERIC OVER ITS COLUMNS, and that is the point. It began as the conversation list, and the
 * moment a second surface wanted the same grid with different columns the choice was to fork it or
 * to describe the columns as data. Forking is how the product ended up with two conversation lists
 * that rendered the same thread unrecognisably differently — so the columns are data, and a new
 * surface supplies a `GridColumn[]` rather than a second implementation of scrolling.
 *
 * VIRTUALIZED, with no "load more". Scrolling asks for the next page as the reader approaches the
 * end and the rows arrive underneath them; the DOM holds a window regardless of how many records
 * exist, so a thousand rows cost what twenty do. A button that must be clicked to see more is the
 * reader doing the paging by hand.
 *
 * IT AUTHORS NO CSS AND CARRIES NO COPY OF ANY. It renders the SAME classes the symbol tree renders
 * — `.vg-wrapper`, `.vg-toolbar`, `.vg-header`, `.vg-row`, `.vg-cell` — which live in
 * `styles/components/base-grid-chrome.scss`. A previous version hand-copied those rules into a Lit
 * stylesheet so it could work inside a shadow root, and within a day the copy had drifted from the
 * original on padding, font size, font weight and border tint: the same list, visibly different on
 * two pages. One definition, in light DOM, is the only arrangement with nothing to keep in step.
 */
import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { Virtualizer, observeElementOffset, observeElementRect, elementScroll } from '@tanstack/virtual-core';
import { ref } from 'lit/directives/ref.js';
import { t } from '../i18n';
import { ColumnResizer } from '../grid';
import { LocaleController } from '../i18n/LocaleController';
import '../elements/vitesse-icon';
/** Row height in px — two lines: the title, and the grey line under it. FIXED, which is what lets
 *  the virtualizer be exact rather than measure every row: a conversation row never expands, so
 *  measuring would buy a layout read per row to learn a number already known. */
const ROW_H = 34;
/** Rows rendered beyond the viewport, so a fast scroll does not outrun the window. */
const OVERSCAN = 8;
/** How close to the end, in rows, before the next page is asked for. Far enough that the fetch is
 *  usually finished before the reader arrives — the point of dropping the button is that paging is
 *  invisible, and a spinner pinned to the bottom edge is the button by another name. */
const PREFETCH_ROWS = 20;
/**
 * Below this many rows, page-scrolling lists render every row in normal flow instead of
 * virtualizing.
 *
 * Virtualizing against an ANCESTOR scroller is the fragile combination: the offsets are relative to
 * a point partway down a taller document, so a stale scroll margin leaves rows positioned inside a
 * spacer that is the full height of the list — which reads as the rows ending and the page carrying
 * on into emptiness. In flow there is no spacer and no margin, so the frame ends where the last row
 * does, by construction.
 *
 * The trade is real and bounded: 300 rows of two spans each is a few hundred nodes, which costs
 * nothing. Past that the virtualizer takes over and the constant-memory guarantee returns.
 */
const FLOW_MAX_ROWS = 300;
/** How narrow a column may be squeezed by default. Roughly a short header plus its sort chevron —
 *  below this the label itself is ellipsed and the column stops saying which one it is. */
const MIN_COL = 64;
/** The flexing column's default floor. Wider than `MIN_COL` because the flexing column holds the
 *  sentence — a title, an objective — and a few words of it is the difference between a row you can
 *  scan and a row you have to open. A surface with a shorter sentence may lower it. */
const FLEX_MIN = 160;
let VitesseRecordGrid = class VitesseRecordGrid extends LitElement {
    constructor() {
        super(...arguments);
        /** The columns, in order. The selector column is added by the grid and is not described here. */
        this.cols = [];
        /** The rows. `null` means "still loading", which is a different thing to say than "none" — an
         *  empty list under a spinner and an empty list under "no conversations yet" are opposite
         *  messages, and collapsing them makes a slow fetch look like an empty account. */
        this.rows = null;
        /**
         * What the expansion costs, in px — a number, or a function of the row.
         *
         * A FUNCTION is usually right: a run that made one tool call and a run that made forty should not
         * reserve the same space, and the host knows which it has. It stays exact either way, because the
         * virtualizer asks per index rather than assuming.
         */
        this.detailHeight = 220;
        /** The last row whose checkbox was clicked, by index in the CURRENT ORDER. Shift-click selects
         *  from there to here — the range people expect from a file list or an IDE, and the reason it is
         *  an index rather than an id: the anchor means "that position in what I am looking at", so
         *  re-sorting between clicks selects what is visibly between them. */
        this.anchor = null;
        /** Show the exact instant rather than "2 days ago". An audit view needs a timestamp it can
         *  cross-reference against a log; a recents panel needs to be skimmable. */
        this.precise = false;
        /** How many the user has IN TOTAL — not how many are loaded. -1 hides it. The header is the
         *  natural home for it now that the list owns one; "30+" cannot answer the question the header is
         *  actually asked, which is how many conversations there are. */
        this.total = -1;
        /**
         * The grid track list, as a CSS `grid-template-columns` value.
         *
         * The HOST declares it because the host renders the cells: header and body align only because
         * both read this one value, so the number of tracks and the number of cells have to agree, and
         * the place that knows both is the surface using the list.
         */
        /**
         * Live column widths in px, for the resizable columns — everything but the selector and the
         * flexing one. Seeded from the columns' declared widths on first render and owned here after,
         * because a drag is a
         * view state: the host declared the starting shape, the reader decides what to do with it.
         */
        this.widths = [];
        /**
         * Which columns the reader has DRAGGED.
         *
         * A width somebody set by hand is a decision; a width the grid chose is a guess. Automatic
         * compaction may squeeze the guesses as the window shrinks, and must not quietly undo the
         * decision — which is exactly what happened when every track was a range: at a narrow width every
         * column already sat at its floor, so raising a track's ceiling by dragging changed nothing at
         * all and the handle felt broken.
         */
        this.pinned = new Set();
        /**
         * Virtualize against the PAGE rather than an inner box.
         *
         * The symbol tree and the log tree have no scroller of their own — they measure against the
         * nearest scrolling ancestor, so the page scrolls as one piece and the page header scrolls away
         * with it. An inner scroller gives you two scrollbars and a frame that stays put while its
         * contents move, which is the difference you feel between the two pages.
         *
         * The rail keeps the inner scroller: it is a fixed-height column with no page to scroll.
         */
        this.pageScroll = false;
        /** Message shown when there are no rows. Hosts differ on what "none" means. */
        this.emptyLabel = '';
        /** Shown while `rows` is null, and while it is filtered to nothing. A grid does not know a
         *  surface's nouns — "no conversations yet" is wrong on a page of agent runs. */
        this.loadingLabel = '';
        this.noMatchLabel = '';
        /** A search is narrowing the list. Keeps "nothing matched" and "you have none" distinguishable —
         *  telling someone mid-search that they have no conversations at all is simply false. */
        this.filtered = false;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- re-renders on locale change
        this.locale = new LocaleController(this);
        /** Bumped by the virtualizer when the visible window moves. */
        this.tick = 0;
        /** Which column orders the list, and which way. Sorting is LOCAL, over the loaded rows: the
         *  server orders by recency and this reorders what is here, which is the honest scope for a
         *  cursor-paginated list — a header that claimed to sort the whole archive would be lying about
         *  rows it has never seen. */
        /** `null` = the server's order, which for this list is most-recent-first. A third click returns
         *  to it: a sort you cannot switch OFF is one you have to remember the original order to undo. */
        this.sortKey = null;
        this.sortDesc = true;
        this.lastAsked = -1;
        /**
         * Watch the end of a flow-rendered list.
         *
         * `rootMargin` reaches BELOW the viewport so the fetch starts before the reader arrives — the
         * point of having no button is that paging is invisible, and a spinner at the bottom edge is the
         * button by another name.
         */
        this.watchEnd = (el) => {
            this.endObserver?.disconnect();
            this.endObserver = undefined;
            if (!el)
                return;
            this.endObserver = new IntersectionObserver((entries) => {
                if (entries.some((e) => e.isIntersecting))
                    this.onReveal?.((this.rows?.length ?? 1) - 1);
            }, { rootMargin: '600px' });
            this.endObserver.observe(el);
        };
    }
    /** LIGHT DOM. The chrome is global SCSS; a shadow root would seal this off from it and force a
     *  private copy, which is exactly the drift this replaced. */
    createRenderRoot() {
        return this;
    }
    willUpdate() {
        this.seedWidths();
    }
    firstUpdated() {
        const scroller = this.renderRoot.querySelector('.scroller');
        if (!scroller)
            return;
        this.virtualizer = new Virtualizer({
            count: this.rows?.length ?? 0,
            getScrollElement: () => scroller,
            scrollMargin: this.scrollMargin(),
            // Exact, not an estimate: every row is ROW_H except the one that is open, which is ROW_H plus
            // whatever the host said its detail costs.
            estimateSize: (i) => {
                const r = this.rows?.[i];
                return r && r.id === this.expanded ? ROW_H + this.detailPx(r) : ROW_H;
            },
            overscan: OVERSCAN,
            observeElementOffset,
            observeElementRect,
            scrollToFn: elementScroll,
            // Keyed by CONTENT, not position: a page appended at the end must not renumber the rows
            // already on screen, and an id survives that where an index does not.
            getItemKey: (i) => this.rows?.[i]?.id ?? String(i),
            onChange: () => {
                this.tick++;
                this.maybeReveal();
            },
        });
        this.cleanup = this.virtualizer._didMount();
        this.virtualizer._willUpdate();
        this.tick++;
    }
    updated(changed) {
        if (!this.virtualizer)
            return;
        if (changed.has('expanded')) {
            // The sizes changed without the count changing, which `measure()` is exactly for. Without this
            // the sizer keeps the old total and the rows below the open one sit under it.
            this.virtualizer.measure();
        }
        if (changed.has('rows')) {
            // The count is the virtualizer's ONLY view of how much there is. Left stale, the sizer keeps
            // the old height and the rows the last page appended are unreachable — the scrollbar simply
            // stops short of them.
            this.virtualizer.setOptions({ ...this.virtualizer.options, count: this.rows?.length ?? 0 });
            this.virtualizer.measure();
        }
        this.virtualizer.setOptions({ ...this.virtualizer.options, scrollMargin: this.scrollMargin() });
        this.virtualizer._willUpdate();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        // The virtualizer holds resize and scroll observers on the scroller. Left attached they keep
        // firing into a detached element, a leak that only ever shows as work nobody asked for.
        this.cleanup?.();
        this.cleanup = undefined;
        this.virtualizer = undefined;
        this.endObserver?.disconnect();
        this.endObserver = undefined;
    }
    /** The nearest ancestor that actually scrolls — the app shell's main column, normally. Same walk
     *  the log tree does, and the same fallback when nothing above is scrollable. */
    pageScroller() {
        for (let el = this.parentElement; el; el = el.parentElement) {
            const oy = getComputedStyle(el).overflowY;
            if (oy === 'auto' || oy === 'scroll')
                return el;
        }
        return document.scrollingElement ?? null;
    }
    /** How far the rows sit below the top of the scroller. Zero for an inner scroller, where the two
     *  tops are the same element. */
    scrollMargin() {
        if (!this.pageScroll || !this.scroller)
            return 0;
        const body = this.renderRoot.querySelector('.cl-sizer');
        if (!body)
            return 0;
        return body.getBoundingClientRect().top - this.scroller.getBoundingClientRect().top + this.scroller.scrollTop;
    }
    /**
     * Tick one row, or a range when shift is held.
     *
     * `click` rather than `change`, because only the click carries the modifier — `change` fires
     * afterwards with no shift key on it, which is why a range has to be decided here.
     *
     * The checkbox's own state has already flipped by the time this runs, so `on` is the state the
     * clicked row is heading to, and the whole range is set to that. Selecting a range to "off" is
     * the same gesture and people use it to clear one.
     */
    pick(id, on, shift) {
        const rows = this.view ?? [];
        const idx = rows.findIndex((r) => r.id === id);
        if (shift && this.anchor !== null && idx >= 0) {
            const [lo, hi] = this.anchor < idx ? [this.anchor, idx] : [idx, this.anchor];
            for (let k = lo; k <= hi; k++)
                this.onSelect?.(rows[k].id, on);
        }
        else {
            this.onSelect?.(id, on);
        }
        // The anchor moves to the row just clicked either way, so a second shift-click extends from
        // where you last were rather than from wherever the first selection of the session began.
        this.anchor = idx;
    }
    /** Ask for the next page as the end comes into view. Guarded by the highest index already asked
     *  for, so the host is not called once per scroll frame with the same number. */
    maybeReveal() {
        if (!this.onReveal || !this.rows?.length)
            return;
        const items = this.virtualizer?.getVirtualItems() ?? [];
        const last = items[items.length - 1]?.index ?? 0;
        const want = Math.min(this.rows.length - 1, last + PREFETCH_ROWS);
        if (want <= this.lastAsked)
            return;
        this.lastAsked = want;
        this.onReveal(want);
    }
    /** The rows in display order. Recomputed per render rather than stored, because storing it means
     *  two sources of truth for "what is in the list" and a bug the day one is updated and the other
     *  is not. */
    get view() {
        if (!this.rows)
            return null;
        if (!this.sortKey)
            return this.rows; // the server's order, untouched
        const col = this.cols.find((c) => c.key === this.sortKey);
        if (!col?.sort)
            return this.rows;
        const dir = this.sortDesc ? -1 : 1;
        return [...this.rows].sort((a, b) => dir * col.sort(a, b));
    }
    render() {
        // The grid's own structure and class names, so these rows ARE the tree's rows rather than
        // rows that resemble them. `--vg-columns` is the track list every one of these rules reads.
        // `vg-scroll-x` because THIS grid's tracks are pixels with floors, where the symbol tree's are
        // percentages that always fit. A frame too narrow for the floors has to be scrollable: the
        // alternative, which the shared chrome does by default, is to clip the rightmost columns away
        // with no scrollbar and nothing on screen to say they exist.
        return html `<div class="vg-wrapper vg-scroll-x" style=${`--vg-columns:${this.trackList()};--vg-min-row:${this.minRowWidth()}px`}>
      <div class="vg-toolbar">
        <!-- The search is the HOST'S. This list does not know what searching means on a given
             surface — one filters a server-side archive, another a loaded page — only where the bar
             goes and what it should look like. -->
        ${this.toolbar ?? nothing}
        <div class="vg-toolbar-actions">
          ${this.actions ?? nothing}
          <!-- The ABSOLUTE total, known from the first page — not "how many are loaded". It sat on
               a header row that no longer exists; the toolbar is the bar that survived. -->
          ${this.total >= 0 ? html `<span class="vg-selected-count">${this.total}</span>` : nothing}
        </div>
      </div>
      ${this.renderHead()}
      <!--
        ROLES, because a virtualised list that declares none is a stack of divs to everything that is
        not a sighted mouse user. A screen reader gets a navigable grid; the assistant's
        what-is-on-screen reads the RENDERED rows and, from aria-rowcount, how many there are in
        total — which is what lets it say "twelve of five thousand" instead of describing a fragment
        as though it were the whole table. The header counts as row 1, so data starts at 2.
      -->
      <div
        class="vg-scroll-container ${this.pageScroll ? 'cl-page-scroll' : ''}"
        role="grid"
        aria-rowcount=${this.view ? this.view.length + 1 : -1}
        aria-colcount=${this.cols.length + 1}
        @scroll=${() => this.maybeReveal()}
      >
        ${this.renderBody()}
      </div>
    </div>`;
    }
    /** The grid template: the selector, the flexing title, then each resizable column at its current
     *  width. One string, read by the header and every row, which is what keeps them aligned. */
    trackList() {
        // The selector, then each column: `minmax(0, 1fr)` for the flexing one, its live width for the
        // rest. `minmax(0, ...)` rather than `1fr` alone because a bare `1fr` refuses to shrink below
        // its content, which turns one long title into a horizontal scrollbar for the whole grid.
        let fixed = -1;
        /*
         * EVERY TRACK IS A RANGE, so the grid compacts instead of losing columns.
         *
         * A FLOOR ON THE FLEXING COLUMN, not `minmax(0, 1fr)`. `1fr` is "whatever is left", and when the
         * fixed columns add up to more than the container that is zero — the column silently collapses
         * to a sliver and the sentence a reader came for disappears, with nothing on screen to say why.
         *
         * A RANGE ON EACH FIXED COLUMN, not a hard px. Its declared width is what it takes when there is
         * room and its ceiling when there is not, so losing 250px to an opened panel narrows all of them
         * a little rather than pushing the last two off the edge. Only once every column is at its floor
         * does the frame scroll — visible and recoverable, where a vanished column is neither.
         *
         * The drag sets the MAX, so a column the reader widened stays wide while space allows and still
         * yields when the window does.
         */
        const tracks = this.cols.map((c) => {
            if (c.width === undefined) {
                // Dragged: exact, like any other pinned column. The space it gives up goes to the spacer
                // appended below rather than being redistributed, because redistributing it would resize a
                // column the reader did not touch.
                if (this.flexWidth !== undefined)
                    return `${Math.max(this.flexWidth, c.min ?? MIN_COL)}px`;
                return `minmax(${c.min ?? FLEX_MIN}px, 1fr)`;
            }
            const i = ++fixed;
            const w = this.widths[i] ?? c.width;
            // A DRAGGED column is exact. The reader asked for that width; honouring it may push the frame
            // past its container, which is visible and scrollable — where silently ignoring the drag is
            // neither, and reads as a broken handle.
            if (this.pinned.has(i))
                return `${w}px`;
            // A FLOOR IS A REQUIREMENT, A WIDTH IS A PREFERENCE, so the floor wins where they disagree.
            // `min` says "below this the content stops being readable"; a declared width under it asks for a
            // column that truncates at every size, which is never what anyone meant. Clamping the floor down
            // to the width — the obvious reading — is how a column ends up permanently ellipsing its longest
            // badge with nothing in the code to show it had been told not to.
            const lo = c.min ?? MIN_COL;
            return `minmax(${lo}px, ${Math.max(w, lo)}px)`;
        });
        // The spacer exists only once nothing flexes. Without it the frame would stop short of its
        // container and the rows would end in a hard edge halfway across the page.
        const spacer = this.flexWidth !== undefined ? ['minmax(0, 1fr)'] : [];
        return ['34px', ...tracks, ...spacer].join(' ');
    }
    /**
     * The narrowest the row can be and still show every column — the sum of the floors.
     *
     * Published as a px `min-width` rather than letting `width: max-content` do it, because
     * max-content resolves the flexing `1fr` track to the LONGEST SENTENCE IN THE DATA: one verbose
     * objective would push the frame 400px wider than the columns need and put every other column
     * behind a scrollbar to no purpose. The sentence is the part that should give.
     */
    minRowWidth() {
        let fixed = -1;
        return this.cols.reduce((sum, c) => {
            if (c.width === undefined)
                return sum + (c.min ?? FLEX_MIN);
            const w = this.widths[++fixed] ?? c.width;
            const lo = c.min ?? MIN_COL;
            return sum + (this.pinned.has(fixed) ? w : Math.max(w, lo));
        }, 34);
    }
    /** Seed the live widths from the columns' declared ones, once. */
    seedWidths() {
        if (this.widths.length)
            return;
        this.widths = this.cols.filter((c) => c.width !== undefined).map((c) => c.width);
    }
    /**
     * Attach the tree's resizer to the header.
     *
     * The SAME class the symbol grid and the log tree use, so the wall, the cursor and the drag feel
     * identical — this element supplies only the arithmetic, which is the part that genuinely differs
     * between a percentage grid, a fixed-px log and this.
     */
    attachResizer() {
        const wrap = this.renderRoot.querySelector('.vg-wrapper');
        const head = this.renderRoot.querySelector('.vg-header');
        if (!wrap || !head || this.resizer)
            return;
        this.resizer = new ColumnResizer(wrap, { highlightTop: () => head.offsetTop });
    }
    /** Begin a drag on the boundary to the RIGHT of resizable column `i`. */
    startResize(e, i) {
        e.preventDefault();
        e.stopPropagation();
        this.attachResizer();
        // Pinned from the first move, not on release: the wall has to follow the pointer while the drag
        // is happening, and a column that only becomes exact at the end would appear stuck until then.
        if (!this.pinned.has(i))
            this.pinned = new Set(this.pinned).add(i);
        const start = this.widths[i];
        this.resizer?.beginDrag(e.clientX, (dx) => {
            // The SAME floor compaction uses: a column dragged to nothing is unreadable and cannot be
            // dragged back, because there is no longer a handle to grab. A column that declared its own
            // floor keeps it here too — dragging is not permission to truncate a badge.
            const next = [...this.widths];
            let seenMin = -1;
            const col = this.cols.find((c) => c.width !== undefined && ++seenMin === i);
            next[i] = Math.max(col?.min ?? MIN_COL, start + dx);
            this.widths = next;
            // +1 for the selector column, and the flexing column sits among them — so the header cell
            // for fixed column `i` is found by counting fixed columns, not by adding a constant.
            let seen = -1;
            const colIdx = this.cols.findIndex((c) => c.width !== undefined && ++seen === i);
            const cell = this.renderRoot.querySelectorAll('.vg-header-cell')[colIdx + 1];
            return cell ? cell.offsetLeft + cell.offsetWidth : 0;
        }, () => undefined);
    }
    /**
     * Drag the flexing column.
     *
     * Its starting width is MEASURED rather than declared, because it never had a declared one — the
     * first drag is what turns "whatever is left" into a number. Measured from the header cell, which
     * is the same element the fixed columns' drag measures against, so the two paths cannot disagree
     * about where the boundary is.
     */
    startFlexResize(e) {
        e.preventDefault();
        e.stopPropagation();
        this.attachResizer();
        const idx = this.cols.findIndex((c) => c.width === undefined);
        if (idx < 0)
            return;
        const cell = this.renderRoot.querySelectorAll('.vg-header-cell')[idx + 1];
        if (!cell)
            return;
        const start = this.flexWidth ?? cell.offsetWidth;
        const floor = this.cols[idx]?.min ?? MIN_COL;
        this.resizer?.beginDrag(e.clientX, (dx) => {
            this.flexWidth = Math.max(floor, start + dx);
            const live = this.renderRoot.querySelectorAll('.vg-header-cell')[idx + 1];
            return live ? live.offsetLeft + live.offsetWidth : 0;
        }, () => undefined);
    }
    renderHead() {
        // One cell per track, in the same order as the row's. The header aligns with the body only
        // because both read `--vg-columns`, so a mismatch here is a silent misalignment.
        //
        // `resizable` is the index into `widths`, which counts only the FIXED columns — the flexing one
        // has no handle, because its width is whatever the others leave it.
        let fixed = -1;
        return html `<div class="vg-header" role="row" aria-rowindex="1">
      <div class="vg-header-cell vg-col-select" role="columnheader">${this.onSelect ? this.renderSelectAll() : nothing}</div>
      ${this.cols.map((c) => {
            // -1 is "the flexing column", which resizes through its own path: there is no entry in
            // `widths` for a column that has no declared width.
            const resizable = c.width === undefined ? -1 : ++fixed;
            const isFlex = c.width === undefined;
            return html `<div
          class="vg-header-cell"
          role="columnheader"
          aria-sort=${this.sortKey === c.key ? (this.sortDesc ? 'descending' : 'ascending') : c.sort ? 'none' : nothing}
          @click=${() => this.sortBy(c.key)}
        >
          <span class="vg-header-label">${c.label}</span>
          ${this.sortKey === c.key
                ? html `<vitesse-icon name=${this.sortDesc ? 'chevron-down' : 'chevron-up'} size="12"></vitesse-icon>`
                : nothing}
          ${resizable >= 0 || isFlex
                ? html `<div
                  class="vg-resize-handle"
                  @pointerdown=${(e) => (isFlex ? this.startFlexResize(e) : this.startResize(e, resizable))}
                  @click=${(e) => e.stopPropagation()}
                ></div>`
                : nothing}
        </div>`;
        })}
    </div>`;
    }
    /** Select-all, the symbol tree's — a checkbox in the header's own select column, not a button in
     *  the page header. It covers what is LOADED: ticking rows the reader has never seen, and then
     *  acting on them, is not something a list should offer. */
    renderSelectAll() {
        const rows = this.rows ?? [];
        const all = rows.length > 0 && rows.every((r) => this.selected?.has(r.id));
        return html `<input
      type="checkbox"
      class="vg-select-all"
      aria-label=${t('grid.selectAll')}
      .checked=${all}
      @click=${(e) => e.stopPropagation()}
      @change=${() => {
            this.anchor = null;
            rows.forEach((r) => this.onSelect?.(r.id, !all));
        }}
    />`;
    }
    /** Click a header: sort by it, or flip the direction if it is already the sort. */
    /** Cycle: first click sorts, second reverses, third clears back to the server's order. */
    sortBy(key) {
        const col = this.cols.find((c) => c.key === key);
        if (!col?.sort)
            return; // an unsortable header is not a control, and must not act like one
        if (this.sortKey !== key) {
            this.sortKey = key;
            this.sortDesc = col.desc ?? true;
        }
        else if (this.sortDesc === (col.desc ?? true)) {
            this.sortDesc = !this.sortDesc; // second click: the other direction
        }
        else {
            this.sortKey = null; // third click: off
        }
        this.virtualizer?.scrollToOffset(0);
    }
    renderBody() {
        if (this.rows === null)
            return html `<div class="vg-empty-state"><div class="vg-empty-title">${this.loadingLabel || t('ai.loadingConversations')}</div></div>`;
        if (this.rows.length === 0) {
            return html `<div class="vg-empty-state">
        <div class="vg-empty-title">
          ${this.filtered ? this.noMatchLabel || t('ai.noConversationsMatch') : this.emptyLabel || t('ai.noConversations')}
        </div>
      </div>`;
        }
        const rows = this.view;
        // FLOW, not absolute positioning, for a short page-scrolling list — see FLOW_MAX_ROWS.
        if (this.pageScroll && rows.length <= FLOW_MAX_ROWS) {
            return html `<div class="cl-flow">
        ${rows.map((r, i) => this.renderRow(r, undefined, i))}
        <!-- The end of the list, watched. In flow there is no virtualizer window and no inner
             scroll, so this is what tells the host to fetch the next page — without it the list
             stops at whatever the first page held. -->
        <div class="cl-sentinel" ${ref(this.watchEnd)}></div>
      </div>`;
        }
        const items = this.virtualizer?.getVirtualItems() ?? [];
        // Before the virtualizer has mounted there is no window, and rendering nothing would show an
        // empty list for a frame. A first screenful covers that without waiting for a second paint.
        const win = items.length ? items : rows.slice(0, OVERSCAN * 3).map((_, i) => ({ index: i, key: rows[i].id, start: i * ROW_H }));
        // DETERMINISTIC, not the virtualizer's measurement. Rows are a fixed height and the count is
        // known, so this is exact — and asking the virtualizer means the spacer inherits whatever it
        // believes about scroll margins, which is how the page ended up scrolling well past the last
        // row into empty space.
        const total = rows.length * ROW_H;
        const margin = this.virtualizer?.options.scrollMargin ?? 0;
        return html `<div class="cl-sizer" style="height:${total}px">
      ${win.map((it) => this.renderRow(rows[it.index], it.start - margin, it.index))}
      <!--
        THE SAME SENTINEL THE FLOW BRANCH USES, and it was missing here — which meant paging stopped
        dead at the flow threshold. Past it, the only thing asking for the next page was maybeReveal,
        driven by the virtualizer's own scroll listener; and when the grid page-scrolls the
        virtualizer's element never scrolls, because an ANCESTOR does. So the trail loaded four
        pages, went quiet, and looked for all the world like a trail with four hundred runs in it.

        An IntersectionObserver does not care which ancestor scrolls, which is the property that
        makes one sentinel correct in both modes.
      -->
      <div class="cl-sentinel" style="position:absolute;top:${Math.max(0, total - ROW_H)}px" ${ref(this.watchEnd)}></div>
    </div>`;
    }
    /**
     * A row, and — when it is the open one — its detail directly beneath it.
     *
     * Rendered as ONE block rather than two siblings so the absolute positioning in the virtualized
     * branch carries both: the row's `top` is where the virtualizer put it, and the detail is simply
     * the rest of that block. Two siblings would need the detail's own offset computed here, which is
     * the arithmetic the virtualizer exists to own.
     */
    /** The declared cost of expanding this row. One place, so the arithmetic the virtualizer does and
     *  the box the browser paints can never be two different numbers. */
    detailPx(r) {
        return typeof this.detailHeight === 'function' ? this.detailHeight(r) : this.detailHeight;
    }
    renderRow(r, top, index) {
        const detail = this.expanded === r.id && this.renderDetail ? this.renderDetail(r) : undefined;
        const row = this.renderRowLine(r, detail === undefined ? top : undefined, index);
        if (detail === undefined)
            return row;
        return html `<div class="vg-row-block" style=${top === undefined ? nothing : `transform:translateY(${top}px)`}>
      ${row}
      <div class="vg-row-detail" style=${`height:${this.detailPx(r)}px`}>${detail}</div>
    </div>`;
    }
    renderRowLine(r, top, index) {
        const on = this.selected?.has(r.id) ?? false;
        // The first column's text, for the checkbox's accessible name — a column of unlabelled
        // checkboxes is unusable with a screen reader, and the row's own identity is the label.
        const label = this.rowLabel?.(r) ?? r.id;
        return html `<div
      class="vg-row ${on ? 'vg-row-selected' : ''}"
      role="row"
      aria-rowindex=${index === undefined ? nothing : index + 2}
      aria-selected=${this.onSelect ? on : nothing}
      style=${top === undefined ? nothing : `transform:translateY(${top}px)`}
      @click=${() => this.onPick?.(r.id)}
    >
      <div class="vg-cell vg-col-select" role="gridcell">
        ${this.onSelect
            ? html `<input
                type="checkbox"
                class="vg-row-checkbox"
                aria-label=${label}
                .checked=${on}
                @click=${(e) => {
                e.stopPropagation();
                this.pick(r.id, e.currentTarget.checked, e.shiftKey);
            }}
              />`
            : nothing}
      </div>
      ${this.cols.map((c) => html `<div class="vg-cell" role="gridcell">${c.cell(r)}</div>`)}
    </div>`;
    }
};
__decorate([
    property({ attribute: false })
], VitesseRecordGrid.prototype, "cols", void 0);
__decorate([
    property({ attribute: false })
], VitesseRecordGrid.prototype, "rows", void 0);
__decorate([
    property({ attribute: false })
], VitesseRecordGrid.prototype, "onPick", void 0);
__decorate([
    property({ attribute: false })
], VitesseRecordGrid.prototype, "onReveal", void 0);
__decorate([
    property({ attribute: false })
], VitesseRecordGrid.prototype, "expanded", void 0);
__decorate([
    property({ attribute: false })
], VitesseRecordGrid.prototype, "renderDetail", void 0);
__decorate([
    property({ attribute: false })
], VitesseRecordGrid.prototype, "detailHeight", void 0);
__decorate([
    property({ attribute: false })
], VitesseRecordGrid.prototype, "selected", void 0);
__decorate([
    property({ attribute: false })
], VitesseRecordGrid.prototype, "onSelect", void 0);
__decorate([
    property({ type: Boolean })
], VitesseRecordGrid.prototype, "precise", void 0);
__decorate([
    property({ type: Number })
], VitesseRecordGrid.prototype, "total", void 0);
__decorate([
    state()
], VitesseRecordGrid.prototype, "widths", void 0);
__decorate([
    state()
], VitesseRecordGrid.prototype, "flexWidth", void 0);
__decorate([
    state()
], VitesseRecordGrid.prototype, "pinned", void 0);
__decorate([
    property({ attribute: false })
], VitesseRecordGrid.prototype, "toolbar", void 0);
__decorate([
    property({ attribute: false })
], VitesseRecordGrid.prototype, "actions", void 0);
__decorate([
    property({ type: Boolean })
], VitesseRecordGrid.prototype, "pageScroll", void 0);
__decorate([
    property()
], VitesseRecordGrid.prototype, "emptyLabel", void 0);
__decorate([
    property()
], VitesseRecordGrid.prototype, "loadingLabel", void 0);
__decorate([
    property()
], VitesseRecordGrid.prototype, "noMatchLabel", void 0);
__decorate([
    property({ attribute: false })
], VitesseRecordGrid.prototype, "rowLabel", void 0);
__decorate([
    property({ type: Boolean })
], VitesseRecordGrid.prototype, "filtered", void 0);
__decorate([
    state()
], VitesseRecordGrid.prototype, "tick", void 0);
__decorate([
    state()
], VitesseRecordGrid.prototype, "sortKey", void 0);
__decorate([
    state()
], VitesseRecordGrid.prototype, "sortDesc", void 0);
VitesseRecordGrid = __decorate([
    customElement('vitesse-record-grid')
], VitesseRecordGrid);
export { VitesseRecordGrid };
//# sourceMappingURL=record-grid.js.map